/**
 * AppealSlice Selectors
 * 
 * Memoized selectors for accessing appeal slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { AppealSliceState } from './state';

// Base selector to get the appeal slice
const selectAppealSlice = (state: RootState): AppealSliceState => state.appeal;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current appeal entity
 */
export const selectCurrentAppeal = createSelector(
  selectAppealSlice,
  (slice) => slice.currentAppeal
);

/**
 * Select whether current appeal exists
 */
export const selectHasCurrentAppeal = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal !== null
);

/**
 * Select the appeal ID (if exists)
 */
export const selectAppealId = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.id
);

/**
 * Select the encounter ID associated with the appeal
 */
export const selectAppealEncounterId = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.encounterId
);

// =============================================================================
// EDITING STATE SELECTORS
// =============================================================================

/**
 * Select whether the appeal is in editing mode
 */
export const selectIsEditing = createSelector(
  selectAppealSlice,
  (slice) => slice.isEditing
);

/**
 * Select whether there are unsaved changes
 */
export const selectHasUnsavedChanges = createSelector(
  selectAppealSlice,
  (slice) => slice.hasUnsavedChanges
);

// =============================================================================
// POLICY REFERENCE SELECTORS
// =============================================================================

/**
 * Select all selected policy references
 */
export const selectSelectedPolicyReferences = createSelector(
  selectAppealSlice,
  (slice) => slice.selectedPolicyReferences
);

/**
 * Select the count of selected policy references
 */
export const selectPolicyReferenceCount = createSelector(
  selectSelectedPolicyReferences,
  (refs) => refs.length
);

/**
 * Select whether any policy references are selected
 */
export const selectHasPolicyReferences = createSelector(
  selectPolicyReferenceCount,
  (count) => count > 0
);

// =============================================================================
// GENERATION STATE SELECTORS
// =============================================================================

/**
 * Select whether AI generation is in progress
 */
export const selectGenerationInProgress = createSelector(
  selectAppealSlice,
  (slice) => slice.generationInProgress
);

// =============================================================================
// ERROR SELECTORS
// =============================================================================

/**
 * Select the current error message
 */
export const selectError = createSelector(
  selectAppealSlice,
  (slice) => slice.error
);

/**
 * Select whether there is an error
 */
export const selectHasError = createSelector(
  selectError,
  (error) => error !== null
);

// =============================================================================
// LETTER DRAFT SELECTORS
// =============================================================================

/**
 * Select the letter draft from the current appeal
 */
export const selectLetterDraft = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.letterDraft
);

/**
 * Select the appeal status
 */
export const selectAppealStatus = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.status
);

/**
 * Select the appeal type
 */
export const selectAppealType = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.appealType
);

/**
 * Select the template type
 */
export const selectTemplateType = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.templateType
);

/**
 * Select the claim context from the current appeal
 */
export const selectClaimContext = createSelector(
  selectCurrentAppeal,
  (appeal) => appeal?.claimContext
);
